/* eslint-disable react-hooks/exhaustive-deps */

import { FC, useState, useEffect, MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { triggerTracking } from '@zoopla/tracking';

import { Envelope } from '@zoopla/assets';
import { selectPageNumber } from '@zoopla/redux-reducer-search-results';
import { alertLinkClickHandler } from '@zoopla/listing-search/nav-with-auth';
import { Links } from '@zoopla/listing-search-context';
import { useIsLoggedIn } from '@zoopla/user';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { Box } from '@zoopla-eng/zds/box';
import { ButtonLink } from '@zoopla-eng/zds/button';
import { LockMedium } from '@zoopla-eng/zds/icons';
import { ControlledDialog } from '@zoopla-eng/zds/dialog';
import {
    contentStyle,
    shareDetailsStyle,
    styledHeadingStyle,
    styledImageStyle
} from './AlertCtaModal.styles.css';

const triggerAlertPopupTracking = (eventLabel: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'Alert pop up',
        eventAction: 'clickthrough',
        eventLabel
    });
};

type AlertCtaModalProps = {
    links: Links;
};

export const AlertCtaModal: FC<React.PropsWithChildren<AlertCtaModalProps>> = ({
    links
}) => {
    const pageNumber = useSelector(selectPageNumber);
    const isLoggedIn = useIsLoggedIn();

    const [isModalShowing, setIsModalShowing] = useState(false);
    const [willShowModal, setWillShowModal] = useState(false);
    const router = useRouter();

    useEffect(() => {
        const showModal =
            pageNumber >= 2 &&
            isLoggedIn === false &&
            typeof window !== 'undefined' &&
            !sessionStorage.getItem('alertsPopupClose');

        setWillShowModal(showModal);
    }, [pageNumber, isLoggedIn, typeof window !== 'undefined']);

    useEffect(() => {
        setIsModalShowing(sessionStorage.getItem('alertsPopupClose') === null);
    }, []);

    useEffect(() => {
        if (willShowModal) {
            triggerAlertPopupTracking('Alert pop up open');
        }
    }, [willShowModal]);

    if (!willShowModal || !isModalShowing) {
        return null;
    }

    const closeModal = () => {
        setIsModalShowing(false);
        sessionStorage.setItem('alertsPopupClose', '1');
    };

    const onButtonClick = (evt: MouseEvent<HTMLAnchorElement>) => {
        evt.preventDefault();

        triggerAlertPopupTracking('Alert pop up click');

        sessionStorage.setItem('alertsPopupClose', '1');
        alertLinkClickHandler({
            evt,
            router,
            requiresAuth: true,
            isLoggedIn
        });
    };

    return (
        <ControlledDialog
            label="Email alerts sign-up"
            isOpen={isModalShowing}
            onClose={closeModal}
        >
            <div className={contentStyle}>
                <img
                    className={styledImageStyle}
                    src={Envelope}
                    alt="Envelope"
                />
                <div className={styledHeadingStyle}>
                    <h4 className={fontGroups.mug}>Sign up for email alerts</h4>
                </div>
                <p className={fontGroups.knife}>
                    Be the first to know about new properties matching your
                    search criteria
                </p>
                <Box py="M100">
                    <ButtonLink
                        category="primary"
                        onClick={onButtonClick}
                        data-url={links?.createAlert || ''}
                    >
                        Create alert
                    </ButtonLink>
                </Box>
                <p className={`${shareDetailsStyle} ${fontGroups.spoon}`}>
                    <LockMedium accessibilityType="decorative" />
                    We will not share your details with third parties
                </p>
            </div>
        </ControlledDialog>
    );
};
